import { Icon, Stack, Typography } from '@ace-clublabs/shared-component-library';
import { useGlobalState } from '../../state/state';
import { getAmountDue, getCurrentMonthlyPaymentDetails, isOnMonthlyBillPlan, isPastDue } from '../../state/helpers';
import { CurrentPayPlan } from '@ace-clublabs/common-membership-api-types';

interface CardTitleProps {
  showIcon?: boolean;
  iconName?: string;
  title: string;
  titleColor: string;
}

export const CardHeaderTitle = () => {
  const [membershipDetails] = useGlobalState('memberDetails');
  const [paymentInfo] = useGlobalState('paymentInfo');
  const [priceCart] = useGlobalState('priceCart');
  const { termExpirationDate, priorTermExpirationDate, hasPaymentInTransit, currentBillPlan, inArrears } =
    membershipDetails;
  const { monthlyPaymentData } = paymentInfo;
  const isMonthlyBillPlan = isOnMonthlyBillPlan(currentBillPlan as CurrentPayPlan);
  const amountDue = getAmountDue(membershipDetails, paymentInfo.monthlyPaymentData, priceCart);
  const expirationDate = inArrears ? priorTermExpirationDate! : termExpirationDate!;

  const getCardTitleProps = (paymentInTransit: boolean, expirationDate: string): CardTitleProps => {
    if (paymentInTransit) {
      return {
        showIcon: true,
        iconName: 'hourglass-half-regular',
        titleColor: '#0C7A93',
        title: 'Payment received and processing',
      };
    } else if (isMonthlyBillPlan) {
      const currentMonthlyPaymentInfo = getCurrentMonthlyPaymentDetails(monthlyPaymentData);
      const {
        monthlyPaymentAmount,
        monthlyPaymentPastDue: isPastDue,
        monthlyPaymentDueDate,
      } = currentMonthlyPaymentInfo;

      if (isPastDue) {
        return {
          showIcon: true,
          iconName: 'triangle-exclamation-regular',
          titleColor: '#B53217',
          title: `Min. payment past due on ${monthlyPaymentDueDate}`,
        };
      } else {
        return {
          showIcon: false,
          titleColor: '#0C7A93',
          title: monthlyPaymentAmount > 0 ? `Min. payment due on ${monthlyPaymentDueDate}` : `No min. payment due`,
        };
      }
    } else {
      if (amountDue === 0) {
        return {
          showIcon: false,
          titleColor: '#0C7A93',
          title: 'Paid in full',
        };
      }
      const isAnnualPaymentPastDue = isPastDue(expirationDate);
      if (isAnnualPaymentPastDue) {
        return {
          showIcon: true,
          iconName: 'triangle-exclamation-regular',
          titleColor: '#B53217',
          title: `Renewal past due on ${expirationDate}`,
        };
      } else {
        return {
          showIcon: false,
          titleColor: '#0C7A93',
          title: `Renewal due on ${expirationDate}`,
        };
      }
    }
  };

  const { showIcon, iconName, title, titleColor } = getCardTitleProps(hasPaymentInTransit, expirationDate);

  return (
    <Stack direction={'row'} gap={'4px'} alignItems={'center'} className="card-header-title-container">
      {showIcon && iconName && (
        <Icon
          iconName={iconName as any}
          className={`${iconName}-icon`}
          sx={{ fontSize: '1.2Rem', color: titleColor }}
        />
      )}
      <Typography variant="b1Regular" sx={{ color: titleColor }} className="card-header-info">
        {title}
      </Typography>
    </Stack>
  );
};
